import { cn } from "@/lib/utils";
import React from "react";
import { ChatMessage } from ".";
import { Typography } from "@/components/custom/typography";

interface AIChatBubbleProps {
  message: ChatMessage;
}

function renderInline(line: string): React.ReactNode {
  const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);

  return parts.map((p, idx) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return (
        <span key={idx} className="font-bold">
          {p.slice(2, -2)}
        </span>
      );
    }
    if (/^\*[^*]+\*$/.test(p)) {
      return (
        <span key={idx} className="italic">
          {p.slice(1, -1)}
        </span>
      );
    }
    if (/^`[^`]+`$/.test(p)) {
      return (
        <code
          key={idx}
          className="px-1 py-0.5 rounded bg-muted text-sm font-mono"
        >
          {p.slice(1, -1)}
        </code>
      );
    }
    return (
      <Typography as="span" tone="medium" key={idx}>
        {p}
      </Typography>
    );
  });
}

function formatContent(text: string, role: string): React.ReactNode {
  const normalized = text.replace(/\\n/g, "\n").replace(/\r\n|\r/g, "\n");
  const paragraphs = normalized.split(/\n{2,}/);

  return paragraphs.map((para, i) => {
    const lines = para.split(/\n/);

    return (
      <div key={i} className="mb-3 last:mb-0">
        {lines.map((line, lineIdx) => {
          // Heading
          const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
          if (headingMatch) {
            const level = headingMatch[1].length;
            const content = headingMatch[2];
            return (
              <Typography
                key={lineIdx}
                as="span"
                className={cn(
                  "font-bold mt-2 mb-1",
                  role === "user" && "text-white",
                  level === 1 && "text-2xl",
                  level === 2 && "text-xl",
                  level === 3 && "text-lg",
                  level >= 4 && "text-base",
                )}
              >
                {content}
              </Typography>
            );
          }

          // Bullet list
          if (/^\s*[-•]\s+/.test(line)) {
            return (
              <ul key={lineIdx} className="list-disc list-inside pl-2">
                <li>{renderInline(line.replace(/^\s*[-•]\s+/, ""))}</li>
              </ul>
            );
          }

          // Normal text
          return (
            <React.Fragment key={lineIdx}>
              <Typography
                as="span"
                className={role === "user" ? "text-white font-semibold " : ""}
              >
                {renderInline(line)}
              </Typography>
              {lineIdx < lines.length - 1 && <br />}
            </React.Fragment>
          );
        })}
      </div>
    );
  });
}

function AIChatBubble({ message }: AIChatBubbleProps) {
  return (
    <div
      className={cn(
        "p-2 rounded-lg max-w-[70%]",
        message.role === "user"
          ? "ml-auto bg-primary-300 "
          : "max-w-full bg-transparent text-foreground",
      )}
    >
      {message.thinking ? (
        <span className="animate-pulse">...</span>
      ) : (
        <div className="space-y-1">
          <Typography>{formatContent(message.text, message.role)}</Typography>
        </div>
      )}
    </div>
  );
}

export default React.memo(AIChatBubble);
